import { useState, useCallback } from 'react';
import { getAllBlogs, getBlog, createBlog, updateBlog, deleteBlog } from '../services/blogApi';

export function useBlog() {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [loadingBlogDetails, setLoadingBlogDetails] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);

  // Aggregate loading state for compatibility with existing consumers
  const loading = loadingBlogs || loadingBlogDetails || loadingAdd || loadingUpdate || loadingDelete;

  // Fetches all blog posts from the API
  const fetchBlogs = useCallback(async () => {
    setLoadingBlogs(true);
    setError(null);
    try {
      const data = await getAllBlogs();
      setBlogs(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch blogs');
    } finally {
      setLoadingBlogs(false);
    }
  }, []);

  // Fetches a single blog post by ID and sets it as selected
  const fetchBlogById = useCallback(async (id) => {
    setLoadingBlogDetails(true);
    setError(null);
    try {
      const data = await getBlog(id);
      setSelectedBlog(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch blog');
      return null;
    } finally {
      setLoadingBlogDetails(false);
    }
  }, []);

  // Creates a new blog post (requires authentication)
  const addBlog = useCallback(async (blog, idToken) => {
    setLoadingAdd(true);
    setError(null);
    try {
      const data = await createBlog(blog, idToken);
      setBlogs((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create blog');
      return null;
    } finally {
      setLoadingAdd(false);
    }
  }, []);

  // Updates an existing blog post (requires authentication)
  // Updates the local blogs list with the modified data
  const updateBlogHook = useCallback(async (id, blog, idToken) => {
    setLoadingUpdate(true);
    setError(null);
    try {
      const updated = await updateBlog(id, blog, idToken);
      const updatedBlog = updated && typeof updated === 'object' ? updated : { ...blog, id };

      setBlogs((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...updatedBlog, id } : item))
      );
      setSelectedBlog((prev) =>
        prev && prev.id === id ? { ...prev, ...updatedBlog, id } : prev
      );
      return true;
    } catch (err) {
      setError(err.message || 'Failed to update blog');
      return false;
    } finally {
      setLoadingUpdate(false);
    }
  }, []);

  // Deletes a blog post (requires authentication)
  // Removes the post from the local blogs list
  const deleteBlogHook = useCallback(async (id, idToken) => {
    setLoadingDelete(true);
    setError(null);
    try {
      await deleteBlog(id, idToken);
      setBlogs((prev) => prev.filter((blog) => blog.id !== id));
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete blog');
      return false;
    } finally {
      setLoadingDelete(false);
    }
  }, []);

  return {
    blogs,
    selectedBlog,
    loading,
    loadingBlogs,
    loadingBlogDetails,
    loadingAdd,
    loadingUpdate,
    loadingDelete,
    error,
    fetchBlogs,
    fetchBlogById,
    addBlog,
    updateBlog: updateBlogHook,
    deleteBlog: deleteBlogHook,
    setSelectedBlog,
  };
}
